/**
 * Income-record endpoints (records are derived from transactions server-side)
 * and the Income Estimator's projection endpoint (GET, server-derived).
 */

import type {
  IncomeBreakdownsResponse,
  IncomeType,
  OptionOutcome,
  PortfolioIncomeProjectionsResponse,
  SplitPortion,
  SplitResolutionResult,
  TaxCategory,
} from '@luminaria/shared';
import { apiClient } from './client';
import { API_ENDPOINTS } from './endpoints';
import { accountIdsParam } from './portfoliosApi';

/** Income record as the API serializes it (Decimal fields as numbers). */
export interface IncomeRecord {
  id: string;
  accountId: string;
  userId: string;
  sourceTransactionId: string;
  type: IncomeType;
  taxCategory: TaxCategory;
  symbol?: string;
  amount: number;
  date: string;
  taxYear: number;
  description?: string;
  createdAt: string;
  updatedAt: string;
  // Option premium fields
  isAssignedOptionPremium?: boolean;
  optionOutcome?: OptionOutcome;
  closingCost?: number;
  netOptionPnL?: number;
}

export interface ResolveOptionParams {
  incomeRecordId: string;
  action: 'expire' | 'close' | 'assign';
  closingCost?: number;
}

interface ResolveOptionResult {
  incomeRecordId: string;
  newOutcome: OptionOutcome;
}

export interface SplitResolveOptionParams {
  incomeRecordId: string;
  portions: SplitPortion[];
}

export interface IncomeConflictResolution {
  sourceTransactionId: string;
  resolution: 'keep_existing' | 'use_incoming';
  incomingData?: {
    sourceTransactionId: string;
    date: string;
    type: string;
    amount: number;
    symbol?: string;
    description?: string;
    taxCategory: string;
    isAssignedOptionPremium?: boolean;
    optionOutcome?: string;
    closingCost?: number;
    netOptionPnL?: number;
  };
}

export const incomeApi = {
  async listByPortfolio(portfolioId: string): Promise<IncomeRecord[]> {
    const { data } = await apiClient.get<{ incomeRecords: IncomeRecord[] }>(API_ENDPOINTS.INCOME.BY_PORTFOLIO(portfolioId));
    return data.incomeRecords ?? [];
  },

  async breakdowns(portfolioId: string, period: number | 'all', excludeNonTaxable: boolean): Promise<IncomeBreakdownsResponse> {
    const { data } = await apiClient.get<IncomeBreakdownsResponse>(API_ENDPOINTS.INCOME.BREAKDOWNS(portfolioId), {
      params: { year: String(period), excludeNonTaxable: String(excludeNonTaxable) },
    });
    return data;
  },

  async resolveOption({ incomeRecordId, action, closingCost }: ResolveOptionParams): Promise<ResolveOptionResult> {
    const { data } = await apiClient.post<ResolveOptionResult>(API_ENDPOINTS.INCOME.RESOLVE_OPTION(incomeRecordId), {
      action,
      closingCost,
    });
    return data;
  },

  async splitResolveOption({ incomeRecordId, portions }: SplitResolveOptionParams): Promise<SplitResolutionResult> {
    const { data } = await apiClient.post<SplitResolutionResult>(API_ENDPOINTS.INCOME.SPLIT_RESOLVE_OPTION(incomeRecordId), {
      portions,
    });
    return data;
  },

  async resolveConflictsBatch(conflicts: IncomeConflictResolution[]): Promise<{ resolved: number; errors: string[] }> {
    const { data } = await apiClient.post<{ resolved: number; errors: string[] }>(API_ENDPOINTS.INCOME.RESOLVE_CONFLICTS_BATCH, {
      conflicts,
    });
    return data;
  },

  /**
   * The Income Estimator's projection for a portfolio (a subset of its
   * accounts when `accountIds` is non-empty); the server derives the holdings
   * and dividend data (WEBD-23).
   */
  async portfolioProjections(input: {
    portfolioId: string;
    accountIds?: readonly string[];
  }): Promise<PortfolioIncomeProjectionsResponse> {
    const { data } = await apiClient.get<PortfolioIncomeProjectionsResponse>(
      API_ENDPOINTS.PORTFOLIOS.INCOME_PROJECTIONS(input.portfolioId),
      { params: accountIdsParam(input.accountIds) }
    );
    return data;
  },
};
